import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import { customFormatMoney, formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  describeStock,
  hasSalesUnit,
  isCompanyListingType,
  priceUnitLine,
  soldAsRow,
} from '../../util/salesUnit';
import { userDisplayNameAsString } from '../../util/data';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_NIGHT,
  LISTING_STATE_CLOSED,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  STOCK_MULTIPLE_ITEMS,
  propTypes,
} from '../../util/types';
import { parse, stringify } from '../../util/urlHelpers';
import { getAuthorWarehouses } from '../../containers/ManageWarehousePage/ManageWarehousePage.duck';
import { useDispatch, useSelector } from 'react-redux';

import {
  AvatarSmall, AvatarMedium, H1, H2, ModalInMobile,
  PrimaryButton, PurchaseButton, SecondaryButton, UserBadge, Button, SocialShareMenu
} from '../../components';

import { createResourceLocatorString } from '../../util/routes';
import { updateFavourites } from '../../containers/SearchPage/SearchPage.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import css from './OrderPanel.module.css';
import ReserveListingButton from './ReserveListingButton/ReserveListingButton';
import { ReactComponent as LocationIcon } from './LocationIcon.svg';
import { ReactComponent as MailIcon } from './MailIcon.svg';
import { ReactComponent as PhoneIcon } from './PhoneIcon.svg';
import { ReactComponent as WhatsAppIcon } from './WhatsAppIcon.svg';

const { Money } = sdkTypes;

const PictureAsPdfIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
    <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9.5 11.5c0 .83-.67 1.5-1.5 1.5H7v2H5.5V9H8c.83 0 1.5.67 1.5 1.5v1zm5-.5c0 1.1-.9 2-2 2h-2V9h2c1.1 0 2 .9 2 2v.5h.5V13h-.5v-.5zm4-1H17v1h1.5V12H17v2h-1.5V9h3v1zM7 11.5h1v-1H7v1z" />
  </svg>
);
const FavoriteIcon = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style={style} aria-hidden="true">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);
const FavoriteBorderIcon = ({ style }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18" style={style} aria-hidden="true">
    <path d="M16.5 3c-1.74 0-3.41.81-4.5 2.09C10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3zm-4.4 15.55l-.1.1-.1-.1C7.14 14.24 4 11.39 4 8.5 4 6.5 5.5 5 7.5 5c1.54 0 3.04.99 3.57 2.36h1.87C13.46 5.99 14.96 5 16.5 5c2 0 3.5 1.5 3.5 3.5 0 2.89-3.14 5.74-7.9 10.05z" />
  </svg>
);


const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

export const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const ListingAttributes = props => {
  const { publicData, listingFieldConfigs, intl } = props;
  const { tireWidthFront, profileFront, diameter, adType, condition, categoryLevel1 } = publicData || {};

  /* BR-4 — stock is the Sharetribe quantity and only company listings track
   * it. Note it is NOT defaulted to 0 here: `undefined` means "this listing
   * has no stock", which must not read as "sold out". */
  const currentStock = props.listing?.currentStock?.attributes?.quantity;
  const stockRow = isCompanyListingType(publicData?.listingType)
    ? describeStock(currentStock, publicData)
    : null;

  const getLabel = (key, value) => {
    const config = listingFieldConfigs?.find(f => f.key === key);
    if (config && config.enumOptions) {
      const option = config.enumOptions.find(o => o.option === value);
      return option ? option.label : value;
    }
    return value;
  };

  const attributes = [
    {
      label: 'Dimension',
      value: tireWidthFront && profileFront && diameter ? `${tireWidthFront}/${profileFront}R${diameter}` : null,
    },
    { label: 'Produkt', value: getLabel('adType', adType) },
    { label: 'Skick', value: getLabel('condition', condition) },
    /* BR-3 — "Säljs som" replaces the old bare "Antal", which meant stock on
     * some listings and set size on others. */
    // Only for listings that are actually sold — not buy requests.
    hasSalesUnit(publicData) && soldAsRow(publicData),
    stockRow,
    { label: 'Bilmärke', value: getLabel('categoryLevel1', categoryLevel1) },
  ].filter(a => a && a.value);

  return (
    <div className={css.attributesGrid}>
      {attributes.map(attr => (
        <div key={attr.label} className={css.attributeItem}>
          <span className={css.attributeLabel}>{attr.label}</span>
          <span className={css.attributeValue}>{attr.value}</span>
        </div>
      ))}
    </div>
  );
};

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);

  /* BR-2 — say in plain words what the price covers. This replaces the generic
   * "per unitType" text, which told a buyer nothing about whether they were
   * looking at one wheel or four. Falls back to the old wording when there is
   * no usable price, or when the listing is not something that is sold at all
   * (a `buying` wanted ad has no sales unit). */
  const unitLine = hasSalesUnit(publicData)
    ? priceUnitLine({
        unitAmount: price.amount,
        publicData,
        formatAmount: amount => customFormatMoney(intl, new Money(amount, price.currency)),
      })
    : null;
  const unitLabel = unitLine || <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />;

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>{unitLabel}</div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>{customFormatMoney(intl, price)}</p>
      <div className={css.perUnit}>{unitLabel}</div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    highlights,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    categories,
    listingFieldConfigs,
    metadata,
    url,
    categoryConfiguration,
    handleViewPDF,
    currentUser,
    hideUtilityButtons = true,
  } = props;
  const authorId = author?.id?.uuid
  const authorMetadata = author.attributes?.profile?.metadata;
  const { isVerified, isTopSeller } = authorMetadata || {};

  const dispatch = useDispatch();
  const routeConfiguration = useRouteConfiguration();
  const { listingWarehouses } = useSelector(
    (state) => state.ManageWareHousePage
  );
  useEffect(() => {
    dispatch(getAuthorWarehouses(authorId));
  }, []);
  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '', warehouse } = publicData || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  const price = listing?.attributes?.price;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const showPriceMissing = isPaymentProcess && !price;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = shouldHaveBookingTime && !isClosed && timeZone;

  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm = isPurchase && typeof currentStock === 'number';

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);



  // const warehouses = listing?.author?.attributes?.profile?.publicData?.warehouses;
  const listingAddress = listing?.attributes?.publicData?.location?.address;
  // const defaultWareHouse = Array.isArray(warehouses) ? warehouses.filter(w => w.isDefault) : [];
  const listingWareHouse = Array.isArray(listingWarehouses)
    ? listingWarehouses.find(item => item.title === warehouse) || listingWarehouses.find(w => w.isDefault)
    : null;
  // const address = defaultWareHouse[0]?.location?.selectedPlace?.address;
  // const email = defaultWareHouse[0]?.email;
  // const phoneNumber = defaultWareHouse[0]?.phoneNumber;
  // const whatsAppNumber = defaultWareHouse[0]?.whatsAppNumber;
  const address = listingWareHouse?.location?.selectedPlace?.address;
  const email = listingWareHouse?.email;
  const phoneNumber = listingWareHouse?.phoneNumber;
  const whatsAppNumber = listingWareHouse?.whatsAppNumber;

  // const [showAuthorInfo, setShowAuthorInfo] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);

  const { SearchPage: searchPageReducer } = useSelector(state => state);
  const { updateFavouritesInProgress } = searchPageReducer || {};

  const [liked, setLiked] = useState(false);
  const userFavouriteListings =
    (currentUser && currentUser.attributes.profile.publicData.favoriteListings) || [];

  useEffect(() => {
    if (isLiked()) {
      setLiked(true);
    }
  }, [userFavouriteListings]);

  const isLiked = () => {
    const likedListings = userFavouriteListings;
    return likedListings && likedListings.includes(listing.id.uuid);
  };

  const toggleFavourite = event => {
    event.preventDefault();
    event.stopPropagation();

    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      history.push(createResourceLocatorString('SignupPage', routeConfiguration, {}, {}), state);
    } else {
      if (updateFavouritesInProgress) return;
      setLiked(!liked);

      const likedListings = userFavouriteListings;
      let newListings = likedListings ? likedListings : [];
      if (isLiked()) {
        newListings = likedListings.filter(l => l !== listing.id.uuid);
      } else {
        newListings.push(listing.id.uuid);
      }
      dispatch(updateFavourites(newListings));
    }
  };

  const secondsMsg = intl.formatMessage({ id: "OrderPanel.secondsMsg" });
  const minutesMsg = intl.formatMessage({ id: "OrderPanel.minutesMsg" });
  const hoursMsg = intl.formatMessage({ id: "OrderPanel.hoursMsg" });
  const daysMsg = intl.formatMessage({ id: "OrderPanel.daysMsg" });
  const weeksMsg = intl.formatMessage({ id: "OrderPanel.weeksMsg" });
  const monthsMsg = intl.formatMessage({ id: "OrderPanel.monthsMsg" });
  const yearsMsg = intl.formatMessage({ id: "OrderPanel.yearsMsg" });

  const ago = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    const weeks = Math.floor(diff / 604800000);
    const months = Math.floor(diff / 2629746000);
    const years = Math.floor(diff / 31556952000);

    if (seconds < 60) return `${seconds} ${secondsMsg}`;
    if (minutes < 60) return `${minutes} ${minutesMsg}`;
    if (hours < 24) return `${hours} ${hoursMsg}`;
    if (days < 7) return `${days} ${daysMsg}`;
    if (weeks < 4) return `${weeks} ${weeksMsg}`;
    if (months < 12) return `${months} ${monthsMsg}`;
    return `${years} ${yearsMsg}`;
  };

  const createdAt = listing?.attributes?.createdAt;
  const time = createdAt ? ago(new Date(createdAt)) : '';

  const isBusinessUser = listing?.author?.attributes?.profile?.publicData?.userType === 'business';

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H2 as="h1" className={css.heading}>{title}</H2>
        </div>

        <div className={css.orderHeading}>
          {titleDesktop ? titleDesktop : <H1 className={titleClasses}>{title}</H1>}
          <div className={css.publishedTime}>
            <FormattedMessage id="OrderPanel.publishedTime" values={{ time: time }} />
          </div>
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div>

        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
        />
        {highlights}
        <ListingAttributes
          publicData={publicData}
          listingFieldConfigs={listingFieldConfigs}
          intl={intl}
          listing={listing}
        />
        <div className={css.author}>
          <AvatarMedium user={author} className={css.providerAvatar} />
          <div className={css.authorInfo}>
            <div className={css.authorNameContainer}>
              <span className={css.providerNameLinked}>
                <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
              </span>
              <UserBadge isVerified={isVerified} isTopSeller={isTopSeller} />
            </div>
            <div className={css.authorType}>
              <FormattedMessage
                id={isBusinessUser ? 'OrderPanel.authorTypeBusiness' : 'OrderPanel.authorTypePrivate'}
              />
            </div>
          </div>
        </div>
        {!hideUtilityButtons && (
          <div className={css.utilityButtons}>
            <SocialShareMenu
              title={title}
              url={url}
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingFieldConfigs}
              categoryConfiguration={categoryConfiguration}
              intl={intl}
              listing={listing}
              price={customFormatMoney(intl, price)}
            />
            <button
              type="button"
              onClick={handleViewPDF}
              className={css.wpActionButton}
            >
              <PictureAsPdfIcon />
              Visa PDF
            </button>

            {!isOwnListing && (
              <button
                type="button"
                onClick={toggleFavourite}
                disabled={updateFavouritesInProgress}
                className={css.wpActionButton}
              >
                {liked ? (
                  <FavoriteIcon style={{ color: '#FF10BC' }} />
                ) : (
                  <FavoriteBorderIcon style={{ color: '#fff' }} />
                )}
                {liked ? 'Sparat' : 'Spara'}
              </button>
            )}
          </div>
        )}
        {/* {showAuthorInfo ? <div className={css.contactInfo}>
          {listingAddress && (
            <div className={css.contentcard}>
              <span className={css.contactIcon}><LocationIcon /></span>
              <a href={`https://www.google.com/maps/search/?q=${encodeURIComponent(listingAddress)}`} target="_blank">
                {listingAddress}
              </a>
            </div>
          )}
          {email && (
            <div className={css.contentcard}>
              <span className={css.contactIcon}><MailIcon />{' '}</span>
              <a href={`mailto:${email}`}>{email}</a>
            </div>
          )}
          {phoneNumber && (
            <div className={css.contentcard}>
              <span className={css.contactIcon}><PhoneIcon />{' '}</span>
              <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
            </div>
          )}
          {whatsAppNumber && (
            <div className={css.contentcard}>
              <span className={css.contactIcon}><WhatsAppIcon />{' '}</span>
              <a href={`https://wa.me/${whatsAppNumber.replace(/\D/g, '')}`} target="_blank" >
                {whatsAppNumber}
              </a>
            </div>
          )}
        </div> : <Button className={css.authorInfoButton} onClick={() => setShowAuthorInfo(true)}>
          Kontaktuppgifter
        </Button>} */}

        {/* <div className={css.contactInfo}>

        
          {listingAddress && (
            <div className={css.contentcard}>
              <span className={css.contactIcon}><LocationIcon /></span>
              <a
                href={`https://www.google.com/maps/search/?q=${encodeURIComponent(listingAddress)}`}
                target="_blank"
              >
                {listingAddress}
              </a>
            </div>
          )}

        
          {email && (
            <>
              {!showEmail ? (
                <SecondaryButton
                  className={css.authorInfoButton}
                  onClick={() => setShowEmail(true)}
                >
                  Visa e-post
                </SecondaryButton>
              ) : (
                <div className={css.contentcard}>
                  <span className={css.contactIcon}><MailIcon /></span>
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
              )}
            </>
          )}

        
          {phoneNumber && (
            <>
              {!showPhone ? (
                <SecondaryButton
                  className={css.authorInfoButton}
                  onClick={() => setShowPhone(true)}
                >
                  Visa telefonnummer
                </SecondaryButton>
              ) : (
                <div className={css.contentcard}>
                  <span className={css.contactIcon}><PhoneIcon /></span>
                  <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                </div>
              )}
            </>
          )}

        
          {whatsAppNumber && (
            <>
              {!showWhatsapp ? (
                <SecondaryButton
                  className={css.authorInfoButton}
                  onClick={() => setShowWhatsapp(true)}
                >
                  Visa WhatsApp
                </SecondaryButton>
              ) : (
                <div className={css.contentcard}>
                  <span className={css.contactIcon}><WhatsAppIcon /></span>
                  <a
                    href={`https://wa.me/${whatsAppNumber.replace(/\D/g, '')}`}
                    target="_blank"
                  >
                    {whatsAppNumber}
                  </a>
                </div>
              )}
            </>
          )}

        </div> */}



        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            listingId={listing.id}
            listing={listing}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            listing={listing}
            isOwnListing={isOwnListing}
            onManageDisableScrolling={onManageDisableScrolling}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
        />

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : isOwnListing ?
          <ReserveListingButton
            listing={listing}
            disabled={isOutOfStock}
            onManageDisableScrolling={onManageDisableScrolling}
          />
          : (() => {
            const CtaButton = isPurchase && !isOutOfStock ? PurchaseButton : PrimaryButton;
            return (
              <>
                <CtaButton
                  onClick={handleSubmit(
                    isOwnListing,
                    isClosed,
                    showInquiryForm,
                    onSubmit,
                    history,
                    location
                  )}
                  disabled={isOutOfStock}
                >
                  {isBooking ? (
                    <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
                  ) : isOutOfStock ? (
                    <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
                  ) : isPurchase ? (
                    <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
                  ) : (
                    <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
                  )}
                </CtaButton>
                {isOutOfStock ? (
                  <p className={css.outOfStockCaption}>
                    <FormattedMessage id="OrderPanel.outOfStockCaption" />
                  </p>
                ) : null}
              </>
            );
          })()}
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,
  categories: array,
  listingFieldConfigs: array,
  metadata: object,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
